import React, { useState, useEffect } from "react";
import {
  Box,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  GridColDef,
} from "@mui/x-data-grid";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import PageTitle from "../../molecules/PageTitle";
import {
  CustomContainer,
  StyledDataGrid,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import NavigateButton from "../../atoms/Button/NavigateButton";

// APIレスポンスの型定義
interface AlertLog {
  ts: number;
  sdcUuid: string;
  notificationCode: string;
  toAddress: string;
}

interface Building {
  autoBuildingId: number,
  buildingId: string,
  buildingManagementId: string,
  buildingName: string,
  buildingAddress: string,
}

const defaultBuilding: Building = {
  autoBuildingId: 0,
  buildingId: '',
  buildingManagementId: '',
  buildingName: '',
  buildingAddress: '',
}

interface Sdc {
  autoSdcId: number;
  sdcUuid: string;
  sdcSerialNumber: string;
  doorPlace: string;
}

interface ApiResponse {
  alertLogs: AlertLog[];
  building: Building;
  sdcs: Sdc[];
}

const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString();
};

const SDCVenderAlertLogPage: React.FC = () => {
  const navigate = useNavigate();
  const { autoBuildingId } = useParams();
  const [data, setData] = useState<ApiResponse>({
    alertLogs: [],
    building: defaultBuilding,
    sdcs: []
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      const jwtToken = await getJwtTokenFromIdToken();
      try {
        // call listAlertEmailLogs API
        const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/buildings/${autoBuildingId}/alert-logs/`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        });

        const responseData: ApiResponse = await response.json();
        setData(responseData);
        setIsLoading(false);
      } catch (error) {
        console.error("Error:", error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [autoBuildingId]);

  const columns: GridColDef[] = [
    {
      field: "ts",
      headerName: "通知日時",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={new Date(params.value as number).toLocaleString()}>
          <span>{formatDate(params.value as number)}</span>
        </Tooltip>
      ),
    },
    {
      field: "sdcSerialNumber",
      headerName: "ドアID",
      flex: 1.5,
    },
    {
      field: "doorPlace",
      headerName: "設置場所",
      flex: 2,
    },
    {
      field: "notificationCode",
      headerName: "エラーコード",
      flex: 2,
    },
    {
      field: "toAddress",
      headerName: "通知送信先",
      flex: 2,
    },
  ];

  const rows = data.alertLogs.map((log, index) => {
    const correspondingSdc = data.sdcs.find(sdc => sdc.sdcUuid === log.sdcUuid) || { sdcSerialNumber: '', doorPlace: '' };
    return {
      id: index,
      ts: log.ts,
      sdcSerialNumber: correspondingSdc.sdcSerialNumber,
      doorPlace: correspondingSdc.doorPlace,
      notificationCode: log.notificationCode,
      toAddress: log.toAddress,
    };
  });

  return (
    <CustomContainer maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={4}>
        <PageTitle title="監視通知ログ" />
        <NavigateButton to={`/buildings/${autoBuildingId}/`} buttonTitle={"物件詳細・編集"} />
      </Box>
      <Box sx={{width: '50%', mb: 2}}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                物件システムID:
              </TableCell>
              <TableCell>{data.building.buildingId}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                取付先コード（物件管理ID）:
              </TableCell>
              <TableCell>{data.building.buildingManagementId}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                取付先名（物件名称）:
              </TableCell>
              <TableCell>{data.building.buildingName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                物件住所:
              </TableCell>
              <TableCell>{data.building.buildingAddress}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      <div style={{ height: 700, width: "100%" }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          localeText={isLoading ? customDataGridLocaleTextLoading : (rows.length === 0 ? customDataGridLocaleTextNoData : {})}
        />
      </div>
    </CustomContainer>
  );
};

export default SDCVenderAlertLogPage;
